/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the hook to use pre-registered patient columns.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/*
 * Used to create a context.
 */
import * as React from 'react';

/*
 * Used to type patients and institutions 
 */
import {
    ALL_INSTITUTIONS_CODE,
    IInstitution
} from '@ngt/opms';


/*
 * Used to format datetimes
 */

import { GridColDef } from '@mui/x-data-grid';
import type { } from '@mui/x-data-grid/themeAugmentation';



import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { ImageUpload, LookupTimePoints, Patient } from '../../api/dtos';
import GlanceImageCell from './GlanceImageCell';


/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Functions
 * ---------------------------------------------------------------------------------
 */

// const StyledTyopography = styled(Typography)(() => ({
//     display: 'flex',
//     alignItems: 'center',
//     width: '100%',
//     height: '100%',

// }));

const useImageUploadColumns = (
    institutions: IInstitution[] | null,
    canAdministerOpms: boolean,
    isSiteUser: boolean,
    isCentralReviewer: boolean
): GridColDef<Patient>[] => {

    const { institutionCode } = useParams();

    const parsedInstitutionCode = useMemo(() => {
            return institutionCode !== ALL_INSTITUTIONS_CODE ? institutionCode : undefined;
        }, [institutionCode]);

    const columns = React.useMemo(() => {

        const c: (GridColDef<ImageUpload> | boolean)[] = [
            {
                field: 'participantNumber',
                headerName: 'Participant Number',
                width: canAdministerOpms ? 165 : 265
            },
            canAdministerOpms && {
                field: 'initials',
                headerName: 'Initials',
                //width: 200
            },
            !parsedInstitutionCode && {
                field: 'institutionName',
                headerName: 'Site Name',
                width: 240
            },
            {
                field: 'baselinemriUploadCount',
                headerName: 'Baseline Uploads',
                filterable: false,
                sortable: false,
                renderCell: rowData => (
                    <GlanceImageCell
                        documents={rowData.row.mriDocuments?.filter(doc => doc.lookupTimePoints === LookupTimePoints.Baseline)}
                    />
                ),
                width: 300
            },
            {
                field: 'eighteenmonthmriUploadCount',
                headerName: '18 Month Uploads',
                filterable: false,
                sortable: false,
                renderCell: rowData => (
                    <GlanceImageCell
                        documents={rowData.row.mriDocuments?.filter(doc => doc.lookupTimePoints === LookupTimePoints.EighteenMonth)}
                    />
                ),
                width: 300
            }
        ];

        return c.filter(column => !!column) as GridColDef<Patient>[];
    }, [institutions, canAdministerOpms, isSiteUser, isCentralReviewer, parsedInstitutionCode]);

    return columns;
};

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */

export default useImageUploadColumns;
